import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import TaskmanagerHeader from '../taskmanagerHeader';
import '../styles/projectsMain.css';
import ProjectWindow from './projectWindow';
import ProjectCard from './projectCard';

const STATUS_TYPES = {
  todo: {
    label: 'To do',
    color: '#6b7280' // Grå
  },
  doing: {
    label: 'Doing',
    color: '#2563eb' // Blå
  },
  pause: {
    label: 'Pause',
    color: '#9333ea' // Lilla
  },
  done: {
    label: 'Done',
    color: '#22c55e' // Grøn
  }
};

function ProjectsMain({ toggleMenu }) {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'projects'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const projectsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProjects(projectsData);
    });

    return () => unsubscribe();
  }, []);

  const handleAddProject = async () => {
    try {
      const newProject = {
        name: 'Nyt projekt',
        description: '',
        responsible: '',
        status: 'todo',
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser.uid,
        updatedAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'projects'), newProject);
    } catch (error) {
      console.error('Fejl ved oprettelse af projekt:', error);
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // Drop blev annulleret
    if (!destination) return;

    // Drop i samme kolonne på samme position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    try {
      // Opdater projektets status i Firestore
      const projectRef = doc(db, 'projects', draggableId);
      await updateDoc(projectRef, {
        status: destination.droppableId,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Fejl ved opdatering af projekt status:', error);
    }
  };

  return (
    <div className="projects-main">
      <TaskmanagerHeader 
        toggleMenu={toggleMenu} 
        onAddProject={handleAddProject}
      />
      
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="projects-content">
          <div className="projects-columns">
            {Object.entries(STATUS_TYPES).map(([status, { label, color }]) => (
              <div 
                key={status} 
                className="status-column"
                data-status={status}
                style={{ '--status-color': color }}
              >
                <div className="column-header">
                  <h3>{label}</h3>
                  <div className="project-count">
                    {projects.filter(project => project.status === status).length}
                  </div>
                </div>
                
                <Droppable droppableId={status}>
                  {(provided, snapshot) => (
                    <div 
                      className={`column-content ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {projects
                        .filter(project => project.status === status)
                        .map((project, index) => (
                          <Draggable 
                            key={project.id} 
                            draggableId={project.id} 
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <ProjectCard
                                project={project}
                                provided={provided}
                                snapshot={snapshot}
                                onClick={() => setSelectedProject(project)}
                              />
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </div>
      </DragDropContext>

      {selectedProject && (
        <ProjectWindow
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </div>
  );
}

export default ProjectsMain;
